import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  BreadcrumbGroup,
  ContentLayout,
  HelpPanel,
  Link,
  SplitPanel,
  Grid,
  Icon,
  Container,
  Header,
  Box,
  SpaceBetween,
  Button,
  Alert,
  StatusIndicator,
  Spinner,
  Cards,
  Tabs,
  TextContent,
  Popover,
  ColumnLayout,
  TextFilter,
  Badge,
  Modal,
  FormField,
  Input,
  Select,
  Table,
} from "@cloudscape-design/components";
import axios from "axios";
import "./Chat.css";
import { useAuth } from "../AuthContext";
import { useNotifications } from "./NotificationContext";
import AppLayoutWrapper from "./AppLayoutWrapper";
import AgentTestWidget from "./AgentTestWidget";
const isVoiceAgentType = (t) => t === 'voice' || !t;

const AgentList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { error: notifyError, success: notifySuccess } = useNotifications();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isToolsOpen, setIsToolsOpen] = useState(false);
  const [isSplitPanelOpen, setIsSplitPanelOpen] = useState(false);
  const [isSplitPanelSide, setIsSplitPanelSide] = useState("Bottom");
  const [isSplitPanelSize, setIsSplitPanelSize] = useState(400);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [activeTabId, setActiveTabId] = useState("details");
  const [filteringText, setFilteringText] = useState("");
  const [agentUsage, setAgentUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [agentSessions, setAgentSessions] = useState(null);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [activityLog, setActivityLog] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [isQuotaModalVisible, setIsQuotaModalVisible] = useState(false);
  const [quotaForm, setQuotaForm] = useState({ voice_minutes: "", text_conversations: "" });
  const [quotaLoading, setQuotaLoading] = useState(false);
  const [quotaPrices] = useState({ voice_minute: 1, text_conversation: 0.05 });
  const [isReassignModalVisible, setIsReassignModalVisible] = useState(false);
  const [reassignTarget, setReassignTarget] = useState(null);
  const [reassignLoading, setReassignLoading] = useState(false);
  const [releaseLoading, setReleaseLoading] = useState(false);
  const [widgetCode, setWidgetCode] = useState(null);
  const [widgetLoading, setWidgetLoading] = useState(false);
  const [redeployLoading, setRedeployLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [deploymentStatuses, setDeploymentStatuses] = useState({});
  const [viewMode, setViewMode] = useState("cards");
  const [sortingColumn, setSortingColumn] = useState(null);
  const [sortingDescending, setSortingDescending] = useState(true);

  const filteredAgents = React.useMemo(() => {
    if (!filteringText) return agents;
    const lower = filteringText.toLowerCase();
    return agents.filter(
      (a) =>
        (a.name && a.name.toLowerCase().includes(lower)) ||
        (a.type && a.type.toLowerCase().includes(lower)) ||
        (a.status && a.status.toLowerCase().includes(lower)) ||
        (a.phone_number && a.phone_number.includes(lower))
    );
  }, [agents, filteringText]);

  const sortedAgents = React.useMemo(() => {
    if (!sortingColumn) return filteredAgents;
    const sorted = [...filteredAgents].sort((a, b) => {
      const getVal = (item) => {
        if (sortingColumn.id === "agent") return item.name?.replace("Assistant - ", "") || "";
        if (sortingColumn.id === "status") return item.status || "";
        if (sortingColumn.id === "type") return item.type || "";
        if (sortingColumn.id === "phone") return item.phone_number || "";
        if (sortingColumn.id === "company") return item.company_name || "";
        if (sortingColumn.id === "created") return item.createdAt || "";
        if (sortingColumn.id === "deployment") return item.deployment_status || "";
        return "";
      };
      const aVal = getVal(a);
      const bVal = getVal(b);
      if (aVal < bVal) return -1;
      if (aVal > bVal) return 1;
      return 0;
    });
    return sortingDescending ? [...sorted].reverse() : sorted;
  }, [filteredAgents, sortingColumn, sortingDescending]);

  // États pour la gestion de l'appel à l'agent (LiveKit)
  const [callStatus, setCallStatus] = useState("idle"); // idle, active, error
  const [isCallLoading, setIsCallLoading] = useState(false);
  const [volumeLevel, setVolumeLevel] = useState(0);
  const [callId, setCallId] = useState(null);

  // Use a ref to track call status for cleanup without causing re-renders
  const callStatusRef = useRef("idle");

  // Update the ref whenever callStatus changes
  useEffect(() => {
    callStatusRef.current = callStatus;
  }, [callStatus]);
  
  const fetchAgents = useCallback(async (force = false) => {
    setLoading(true);
    try {
      const accessToken = user?.access_token || user?.token;
      if (!accessToken) {
        throw new Error("No access token available");
      }

      const url = force ? `/api/agents/list/?t=${new Date().getTime()}` : "/api/agents/list/";
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });

      console.log(force ? "API Response (refresh):" : "API Response:", response);
      if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
        console.log("First agent data:", JSON.stringify(response.data[0], null, 2));
      }

      if (response?.data && Array.isArray(response.data)) {
        if (response.data.length === 0) {
          setAgents([]);
        } else {
          const agentsWithKey = response.data.map((agent, idx) => ({
            ...agent,
            _key: agent.id || agent.adexcloud_agent_id || agent.creation_id || `agent-${idx}`,
          }));
          setAgents(agentsWithKey);
        }
      } else {
        setError("Invalid response format");
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
      if (error.response?.status === 401) {
        setError("Your session has expired. Please log in again.");
      } else {
        setError(error.message || "Failed to load agents");
      }
      setAgents([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const forceRefreshAgents = useCallback(async () => {
    console.log("Forçage du rechargement des données des agents");
    await fetchAgents(true);
  }, [fetchAgents]);

  useEffect(() => {
    if (user) {
      fetchAgents();
    }
  }, [user, fetchAgents]);

  const handleAgentSelect = (agent) => {
    setSelectedAgent(agent);
    setIsSplitPanelOpen(true);
    setAgentUsage(null);
    setAgentSessions(null);
    setActivityLog([]);
    setWidgetCode(null);
    fetchAgentUsage(agent);
    fetchAgentSessions(agent);
    fetchActivityLog(agent);
    fetchWidgetCode(agent);
    const agentId = agent.id || agent.adexcloud_agent_id;
    if (agentId) {
      fetchDeploymentStatus(agentId);
    }
  };

  const fetchAgentUsage = async (agent) => {
    if (!agent) return;
    const agentId = agent.id || agent.adexcloud_agent_id;
    if (!agentId) return;
    setUsageLoading(true);
    try {
      const accessToken = user?.access_token || user?.token || localStorage.getItem("access_token");
      const res = await axios.get(`/api/agents/${agentId}/consumption/`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (res.data?.status === "success") {
        setAgentUsage(res.data.data);
      }
    } catch (err) {
      console.error("Error fetching agent usage:", err);
    } finally {
      setUsageLoading(false);
    }
  };

  const fetchAgentSessions = async (agent) => {
    if (!agent) return;
    const agentId = agent.id || agent.adexcloud_agent_id;
    if (!agentId) return;
    setSessionsLoading(true);
    try {
      const accessToken = user?.access_token || user?.token || localStorage.getItem("access_token");
      const res = await axios.get(`/api/agents/${agentId}/sessions/`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (res.data?.status === "success") {
        setAgentSessions(res.data.data);
      }
    } catch (err) {
      console.error("Error fetching agent sessions:", err);
    } finally {
      setSessionsLoading(false);
    }
  };

  const fetchActivityLog = async (agent) => {
    if (!agent) return;
    const agentId = agent.id || agent.adexcloud_agent_id;
    if (!agentId) return;
    setActivityLoading(true);
    try {
      const accessToken = user?.access_token || user?.token || localStorage.getItem("access_token");
      const res = await axios.get(`/api/agents/${agentId}/activity-log/?limit=50`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (res.data?.status === "success") {
        setActivityLog(res.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching activity log:", err);
    } finally {
      setActivityLoading(false);
    }
  };

  const fetchWidgetCode = async (agent) => {
    if (!agent) return;
    const agentId = agent.id || agent.adexcloud_agent_id;
    if (!agentId) return;
    setWidgetLoading(true);
    try {
      const accessToken = user?.access_token || user?.token || localStorage.getItem("access_token");
      const res = await axios.get(`/api/agents/${agentId}/widget-code/`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (res.data?.widget_code) {
        setWidgetCode(res.data.widget_code);
      } else {
        setWidgetCode(null);
      }
    } catch (err) {
      console.error("Error fetching widget code:", err);
      setWidgetCode(null);
    } finally {
      setWidgetLoading(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      notifySuccess('Copied to clipboard');
    } catch (err) {
      console.error('Copy failed:', err);
      notifyError('Unable to copy');
    }
  };

  const handleReleasePack = async () => {
    if (!selectedAgent) return;
    const agentId = selectedAgent.id || selectedAgent.adexcloud_agent_id;
    if (!agentId) return;
    setReleaseLoading(true);
    try {
      const accessToken = user?.access_token || user?.token || localStorage.getItem("access_token");
      const res = await axios.post(
        `/api/agents/${agentId}/release-pack/`,
        {},
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      if (res.data?.status === "success") {
        await fetchAgentUsage(selectedAgent);
        fetchActivityLog(selectedAgent);
        forceRefreshAgents();
      } else {
        setError(res.data?.message || "Failed to release pack");
      }
    } catch (err) {
      console.error("Release pack error:", err);
      setError(err.response?.data?.message || "Failed to release pack");
    } finally {
      setReleaseLoading(false);
    }
  };

  const openReassignModal = () => {
    setReassignTarget(null);
    setIsReassignModalVisible(true);
  };

  const handleReassignPack = async () => {
    if (!selectedAgent || !reassignTarget || !agentUsage?.pack?.purchase_id) return;
    const targetAgentId = reassignTarget.value;
    if (!targetAgentId) return;
    setReassignLoading(true);
    try {
      const accessToken = user?.access_token || user?.token || localStorage.getItem("access_token");
      const res = await axios.post(
        `/api/agents/${targetAgentId}/reassign-pack/`,
        { purchase_id: agentUsage.pack.purchase_id },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      if (res.data?.status === "success") {
        setIsReassignModalVisible(false);
        setReassignTarget(null);
        await fetchAgentUsage(selectedAgent);
        fetchActivityLog(selectedAgent);
        forceRefreshAgents();
      } else {
        setError(res.data?.message || "Failed to reassign pack");
      }
    } catch (err) {
      console.error("Reassign pack error:", err);
      setError(err.response?.data?.message || "Failed to reassign pack");
    } finally {
      setReassignLoading(false);
    }
  };

  const handleBuyQuota = async () => {
    if (!selectedAgent) return;
    const agentId = selectedAgent.id || selectedAgent.adexcloud_agent_id;
    if (!agentId) return;
    setQuotaLoading(true);
    try {
      const accessToken = user?.access_token || user?.token || localStorage.getItem("access_token");
      const res = await axios.post(
        `/api/agents/${agentId}/buy-quota/`,
        {
          voice_minutes: parseFloat(quotaForm.voice_minutes) || 0,
          text_conversations: parseInt(quotaForm.text_conversations, 10) || 0,
        },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      if (res.data?.status === "success") {
        setAgentUsage(res.data.data.usage);
        setIsQuotaModalVisible(false);
        setQuotaForm({ voice_minutes: "", text_conversations: "" });
        await fetchAgentUsage(selectedAgent);
        fetchActivityLog(selectedAgent);
      } else {
        setError(res.data?.message || "Failed to buy quota");
      }
    } catch (err) {
      console.error("Buy quota error:", err);
      setError(err.response?.data?.message || "Failed to buy quota");
    } finally {
      setQuotaLoading(false);
    }
  };

  const estimatedQuotaCost = () => {
    const voice = parseFloat(quotaForm.voice_minutes) || 0;
    const text = parseInt(quotaForm.text_conversations, 10) || 0;
    return (voice * quotaPrices.voice_minute + text * quotaPrices.text_conversation).toFixed(2);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString();
  };

  // Function to get status badge color
  const getStatusBadgeType = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
      case "success":
        return "success";
      case "inactive":
      case "pending":
        return "warning";
      case "error":
      case "failed":
        return "error";
      default:
        return "info";
    }
  };

  // Function to get deployment status badge type
  const getDeploymentBadgeType = (depStatus) => {
    switch (depStatus?.toLowerCase()) {
      case "deployed":
        return "success";
      case "deployment_failed":
      case "failed":
        return "error";
      case "stopped":
        return "warning";
      case "not_deployed":
      default:
        return "info";
    }
  };

  // Fetch deployment status for an agent
  const fetchDeploymentStatus = async (agentId) => {
    try {
      const accessToken = user?.access_token || user?.token || localStorage.getItem("access_token");
      const res = await axios.get(`/api/agents/${agentId}/deployment-status/`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (res.data?.status === "success") {
        const data = res.data.data;
        setDeploymentStatuses((prev) => ({
          ...prev,
          [agentId]: data,
        }));
        // Sync selectedAgent.deployment_status with the effective status from remote
        const effectiveStatus = data.deployment_status;
        if (effectiveStatus) {
          setSelectedAgent((prev) => {
            if (!prev) return prev;
            const currentAgentId = prev.id || prev.adexcloud_agent_id;
            if (currentAgentId === agentId && prev.deployment_status !== effectiveStatus) {
              return { ...prev, deployment_status: effectiveStatus };
            }
            return prev;
          });
        }
      }
    } catch (e) {
      console.warn("Failed to fetch deployment status for", agentId, e);
    }
  };

  // Redeploy an agent
  const handleRedeploy = async () => {
    if (!selectedAgent) return;
    const agentId = selectedAgent.id || selectedAgent.adexcloud_agent_id;
    if (!agentId) return;
    setRedeployLoading(true);
    // Mark as deploying immediately
    setSelectedAgent((prev) => ({ ...prev, deployment_status: "deploying" }));
    setDeploymentStatuses((prev) => ({
      ...prev,
      [agentId]: { ...prev[agentId], deployment_status: "deploying" },
    }));
    try {
      const accessToken = user?.access_token || user?.token || localStorage.getItem("access_token");
      const res = await axios.post(`/api/agents/${agentId}/redeploy/`, {}, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (res.data?.status === "success") {
        notifySuccess(res.data.message || "Agent redeployed successfully");
        // Poll deployment status since adexcloud deploy is async
        const pollDeployment = async () => {
          for (let i = 0; i < 12; i++) {
            await new Promise((resolve) => setTimeout(resolve, 5000));
            try {
              const statusRes = await axios.get(`/api/agents/${agentId}/deployment-status/`, {
                headers: { Authorization: `Bearer ${accessToken}` },
              });
              if (statusRes.data?.status === "success") {
                const data = statusRes.data.data;
                setDeploymentStatuses((prev) => ({ ...prev, [agentId]: data }));
                const effectiveStatus = data?.deployment_status;
                if (effectiveStatus === "deployed") {
                  setSelectedAgent((prev) => ({ ...prev, deployment_status: "deployed" }));
                  notifySuccess("Agent is now deployed and running.");
                  return;
                }
                if (effectiveStatus === "deployment_failed" || effectiveStatus === "stopped") {
                  setSelectedAgent((prev) => ({ ...prev, deployment_status: effectiveStatus }));
                  notifyError(`Agent deployment status: ${effectiveStatus}`);
                  return;
                }
                // Still deploying — update status
                setSelectedAgent((prev) => ({ ...prev, deployment_status: effectiveStatus || "deploying" }));
              }
            } catch (pollErr) {
              console.warn("Deployment status poll error:", pollErr);
            }
          }
          // Timeout after 60s — fetch final status from backend
          fetchDeploymentStatus(agentId);
        };
        pollDeployment();
      } else {
        notifyError(res.data?.message || "Failed to redeploy agent");
        setSelectedAgent((prev) => ({ ...prev, deployment_status: "deployment_failed" }));
      }
    } catch (e) {
      const msg = e.response?.data?.message || e.message;
      notifyError(`Redeploy failed: ${msg}`);
      setSelectedAgent((prev) => ({ ...prev, deployment_status: "deployment_failed" }));
    } finally {
      setRedeployLoading(false);
    }
  };

  // Fonction pour démarrer un appel à l'agent via LiveKit (backend token)
  const startAgentCall = async () => {
    if (selectedAgent && (selectedAgent.id || selectedAgent.adexcloud_agent_id)) {
      try {
        setIsCallLoading(true);
        console.log("Starting call with agent:", selectedAgent.id);

        const assistantExternalId = selectedAgent.adexcloud_agent_id || selectedAgent.assistant_external_id;
        if (!assistantExternalId) {
          console.error("No adexcloud agent ID found for this agent");
          setIsCallLoading(false);
          setCallStatus("error");
          notifyError("Cannot start call: Agent is not properly configured");
          return;
        }

        // Fetch LiveKit token from backend
        const tokenRes = await axios.post(
          "/api/agent/test-call/",
          { agent_id: selectedAgent.id || selectedAgent.adexcloud_agent_id },
          { withCredentials: true }
        );
        const { token, livekit_url, room } = tokenRes.data;
        setCallId(room);
        setCallStatus("active");
        setIsCallLoading(false);

        // TODO: connect with LiveKit SDK once livekit-client is installed
        console.log("LiveKit token received:", token, "url:", livekit_url, "room:", room);
      } catch (error) {
        console.error("Failed to start call:", error);
        setCallStatus("error");
        setIsCallLoading(false);
        notifyError("Unable to start call: " + (error.message || "Unknown error"));
      }
    } else {
      console.error("Cannot start call: no agent selected");
    }
  };

  // Sync agent state with adexcloudai
  const handleSyncAgent = async () => {
    if (!selectedAgent) return;
    const agentId = selectedAgent.id || selectedAgent.adexcloud_agent_id;
    if (!agentId) return;
    setSyncLoading(true);
    try {
      const accessToken = user?.access_token || user?.token || localStorage.getItem("access_token");
      const res = await axios.post(`/api/agents/${agentId}/sync/`, {}, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (res.data?.status === "success") {
        const data = res.data.data;
        const changes = data.changes || [];
        if (changes.length > 0) {
          notifySuccess(`Synced with adexcloudai: ${changes.join(", ")}`);
        } else {
          notifySuccess("Agent is already in sync with adexcloudai");
        }
        // Update selectedAgent with synced values
        setSelectedAgent((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            status: data.status,
            deployment_status: data.deployment_status,
          };
        });
        // Refresh agent list and usage
        forceRefreshAgents();
        if (selectedAgent) {
          fetchAgentUsage(selectedAgent);
          fetchActivityLog(selectedAgent);
          const depId = selectedAgent.id || selectedAgent.adexcloud_agent_id;
          if (depId) fetchDeploymentStatus(depId);
        }
      } else {
        notifyError(res.data?.message || "Failed to sync agent");
      }
    } catch (err) {
      console.error("Sync agent error:", err);
      notifyError(err.response?.data?.message || "Failed to sync agent with adexcloudai");
    } finally {
      setSyncLoading(false);
    }
  };

  // Delete agent handler
  const handleDeleteAgent = async (agentId) => {
    try {
      if (!agentId) {
        notifyError("Cannot delete: agent has no valid ID");
        return;
      }
      const accessToken = user?.access_token || user?.token;
      await axios.delete(`/api/agents/${agentId}/delete/`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      setAgents((prev) => prev.filter((a) => (a.id || a.adexcloud_agent_id) !== agentId));
      setSelectedItems([]);
      setSelectedAgent(null);
      setIsSplitPanelOpen(false);
    } catch (err) {
      console.error("Delete agent error:", err);
      notifyError("Failed to delete agent: " + (err.response?.data?.message || err.message));
    }
  };

  // Fonction pour arrêter un appel en cours
  const stopAgentCall = () => {
    console.log("stopAgentCall called. Current callId:", callId);
    try {
      // TODO: disconnect LiveKit room when SDK is installed
      setCallStatus("idle");
      setVolumeLevel(0);
      console.log("Call stopped successfully");
    } catch (error) {
      console.error("Error stopping call:", error);
      setCallStatus("idle");
      setVolumeLevel(0);
    }
  };
  
  // Fonction pour générer l'animation de la bulle basée sur le niveau de volume
  const generateWaveBars = (volumeLevel) => {
    return (
      <div className="voice-circle-container">
        <div
          className="main-circle"
          style={{
            transform: `scale(${0.6 + volumeLevel * 0.6})`,
            background: `radial-gradient(circle at 65% 35%, 
rgb(8, 112, 76) 0%, 
rgb(13, 109, 90) ${40 + volumeLevel * 30}%, 
rgb(13, 80, 62) 120%)`,
          }}
        >
          <div className="inner-pulse" />
          <div className="sonar-wave" />
          <div className="particle-overlay" />
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Box textAlign="center" padding={{ top: "xxxl" }}>
        <Spinner size="large" />
        <Box variant="h3" padding={{ top: "m" }}>
          Loading agents...
        </Box>
      </Box>
    );
  }

  const totalAgents = agents.length;
  const activeAgents = agents.filter((a) => a.status === 'active' || a.status === 'success').length;
  const agentsWithPhone = agents.filter((a) => isVoiceAgentType(a.ai_agent_type) && a.phone_number && a.phone_number !== "Not assigned").length;
  const recentlyCreated = agents.filter((a) => {
    const created = a.created_at ? new Date(a.created_at) : null;
    return created && (new Date() - created) < 7 * 24 * 60 * 60 * 1000;
  }).length;

  // Normalize agent type: adexcloud.ai returns "text_only", local model uses "text"
  const isVoiceAgent = selectedAgent && (selectedAgent.ai_agent_type === 'voice' || !selectedAgent.ai_agent_type);
  const isTextAgent = selectedAgent && (selectedAgent.ai_agent_type === 'text' || selectedAgent.ai_agent_type === 'text_only');

  if (error) {
    return (
      <Alert type="error" header="Error loading agents">
        {error}
      </Alert>
    );
  }

  return (
    <>
    <AppLayoutWrapper
      disableContentPaddings
      toolsHide={false}
      splitPanelPreferences={{ position: isSplitPanelSide }}
      splitPanelSize={isSplitPanelSize}
      splitPanelOpen={isSplitPanelOpen && selectedAgent !== null}
      onSplitPanelToggle={() => setIsSplitPanelOpen(!isSplitPanelOpen)}
      onSplitPanelPreferencesChange={({ detail }) =>
        setIsSplitPanelSide(detail.position)
      }
      onSplitPanelResize={({ detail }) => setIsSplitPanelSize(detail.size)}
      toolsOpen={isToolsOpen}
      onToolsChange={({ detail }) => setIsToolsOpen(detail.open)}
      tools={
        <HelpPanel
          header={<Header variant="h2">Agent Information</Header>}
        >
          <SpaceBetween size="m">
            <TextContent>
              <h3>Agent Management</h3>
              <p>
                This page displays all your Adexgénie AI agents. Select an agent to view its details
                and manage its settings.
              </p>
              
              <h4>Agent Card Information</h4>
              <ul>
                <li><strong>Name:</strong> The name of your agent</li>
                <li><strong>Type:</strong> The type of agent (Receptionist, Support, etc.)</li>
                <li><strong>Status:</strong> Current operational status</li>
                <li><strong>Phone Number:</strong> Dedicated phone number for this agent</li>
                <li><strong>Created:</strong> When the agent was created</li>
                <li><strong>Voice:</strong> The voice used by your agent</li>
              </ul>
              
              <h4>Agent Details Panel</h4>
              <p>
                Click on any agent card to open the details panel, where you can:
              </p>
              <ul>
                <li>View complete agent information</li>
                <li>Check call and chat statistics</li>
                <li>View agent system prompt</li>
                <li>Manage agent settings</li>
                <li>Test your agent</li>
              </ul>
            </TextContent>
          </SpaceBetween>
        </HelpPanel>
      }
      content={
        <div>
          <ContentLayout
            breadcrumbs={
              <BreadcrumbGroup
                items={[
                  { text: "Home", href: "/dashboard" },
                  { text: "Agents", href: "/agents" },
                ]}
                onFollow={(event) => {
                  event.preventDefault();
                  navigate(event.detail.href);
                }}
              />
            }
            defaultPadding
            headerBackgroundStyle="linear-gradient(to bottom, rgba(9, 29, 43, 1) 2%, rgba(0, 108, 224, 1) 8%, rgba(255, 255, 255, 1) 82%)"
            header={
              <Header
                variant="h1"
                counter={`(${filteredAgents.length})`}
                info={
                  <Link variant="info" onFollow={() => setIsToolsOpen(true)}>
                    Info
                  </Link>
                }
                actions={
                  <SpaceBetween size="xs" direction="horizontal">
                    <Button
                      iconName="grid-view"
                      variant={viewMode === "cards" ? "primary" : "normal"}
                      onClick={() => setViewMode("cards")}
                    >
                      Cards
                    </Button>
                    <Button
                      iconName="list-view"
                      variant={viewMode === "table" ? "primary" : "normal"}
                      onClick={() => setViewMode("table")}
                    >
                      Table
                    </Button>
                    <Button iconName="refresh" onClick={forceRefreshAgents}>
                      Refresh
                    </Button>
                    <Button variant="primary" iconName="add-plus" onClick={() => navigate('/Wizard')}>
                      Create Agent
                    </Button>
                  </SpaceBetween>
                }
                description="View, manage and test your Adexgénie AI agents."
              >
                Agents
              </Header>
            }
          >
            <SpaceBetween size="l" padding={{ top: "l", bottom: "l" }}>
              <ColumnLayout columns={{ default: 1, s: 2, m: 4 }} variant="text-grid">
                <Container>
                  <Box variant="awsui-key-label">Total Agents</Box>
                  <Box variant="h1">{totalAgents}</Box>
                </Container>
                <Container>
                  <Box variant="awsui-key-label">Active</Box>
                  <Box variant="h1" color="text-status-success">{activeAgents}</Box>
                </Container>
                <Container>
                  <Box variant="awsui-key-label">With Phone</Box>
                  <Box variant="h1">{agentsWithPhone}</Box>
                </Container>
                <Container>
                  <Box variant="awsui-key-label">New (7d)</Box>
                  <Box variant="h1">{recentlyCreated}</Box>
                </Container>
              </ColumnLayout>

              <TextFilter
                filteringText={filteringText}
                filteringPlaceholder="Find agents by name, type, status or phone"
                onChange={({ detail }) => setFilteringText(detail.filteringText)}
              />
              {viewMode === "cards" && (
              <Grid
                gridDefinition={[
                  { colspan: { default: 12 } }
                ]}
              >
                <Cards
                  onSelectionChange={({ detail }) => {
                    setSelectedItems(detail?.selectedItems ?? []);
                    if (detail.selectedItems.length > 0) {
                      handleAgentSelect(detail.selectedItems[0]);
                    }
                  }}
                  entireCardClickable
                  selectedItems={selectedItems}
                  cardDefinition={{
                    header: (item) => (
                      <div>
                        <Link fontSize="heading-m" fontWeight="bold">
                          {item.name.replace("Assistant - ", "")}
                        </Link>
                        <div style={{ fontSize: "14px", color: "#5f6b7a", marginTop: "4px" }}>
                          {item.service_type || "General Assistant"}
                        </div>
                      </div>
                    ),
                    sections: [
                      {
                        id: "image",
                        content: (item) => {
                          // Map business type to local image in public/
                          const getBusinessImage = (typeName) => {
                            if (!typeName) return "/agent_receptionnist.png";
                            const t = typeName.toLowerCase();
                            if (t.includes("recept")) return "/agent_receptionnist.png";
                            if (t.includes("support")) return "/agent_support.png";
                            if (t.includes("sales") || t.includes("commerc")) return "/agent_commerciale.png";
                            return "/agent_receptionnist.png";
                          };

                          // Badge icon mapping based on business type name
                          const getTypeIcon = (typeName) => {
                            if (!typeName) return "user-profile";
                            const t = typeName.toLowerCase();
                            if (t.includes("recept")) return "call";
                            if (t.includes("support")) return "contact";
                            if (t.includes("sales") || t.includes("commerc")) return "status-positive";
                            return "user-profile";
                          };

                          const mainImage = getBusinessImage(item.service_type || item.type);
                          const badgeIcon = getTypeIcon(item.type);

                          return (
                            <div style={{ display: "flex", alignItems: "center", position: "relative" }}>
                              <div style={{ position: "relative" }}>
                                <img
                                  src={mainImage}
                                  alt={item.service_type || item.type}
                                  style={{
                                    height: "80px",
                                    width: "80px",
                                    objectFit: "contain",
                                    marginRight: "16px",
                                    borderRadius: "8px",
                                    backgroundColor: "#f2f8ff"
                                  }}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                                {/* Badge de type de service */}
                                <div style={{
                                  position: "absolute",
                                  bottom: "-10px",
                                  right: "8px",
                                  width: "48px",
                                  height: "48px",
                                  borderRadius: "50%",
                                  backgroundColor: "#ffffff",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  boxShadow: "0 2px 5px rgba(0, 0, 0, 0.25)",
                                  border: "2px solid #ffffff"
                                }}>
                                  <Icon name={badgeIcon} size="big" variant="subtle" />
                                </div>
                              </div>
                              <div>
                                <div style={{ 
                                  display: "flex", 
                                  alignItems: "center", 
                                  transform: "scale(1.3)",
                                  transformOrigin: "left center",
                                  marginLeft: "4px"
                                }}>
                                  <StatusIndicator 
                                    type={getStatusBadgeType(item.status)}
                                    iconAriaLabel={item.status || "Active"}
                                    wrapText={false}
                                  >
                                    <span style={{ 
                                      fontSize: "16px", 
                                      fontWeight: "600",
                                      marginLeft: "4px"
                                    }}>
                                      {item.status || "Active"}
                                    </span>
                                  </StatusIndicator>
                                </div>
                              </div>
                            </div>
                          );
                        }
                      },
                      {
                        id: "details",
                        content: (item) => {
                          // Définir les éléments à afficher dans la grille
                          const gridItems = [
                            {
                              label: "Business Type",
                              value: item.type
                            },
                            {
                              label: "Service Type",
                              value: item.service_type || "General Assistant"
                            },
                            {
                              label: "Pack",
                              value: item.pack_name || (item.pack ? item.pack.name : "N/A")
                            },
                            {
                              label: "Phone Number",
                              value: (
                                <div style={{ display: "flex", alignItems: "center" }}>
                                  <Icon name="phone" />
                                  <span style={{ marginLeft: "8px" }}>{item.phone_number || "N/A"}</span>
                                </div>
                              )
                            },
                            {
                              label: "Company",
                              value: item.company_name || "N/A"
                            },
                            {
                              label: "Created",
                              value: formatDate(item.createdAt)
                            },
                            {
                              label: "Status",
                              value: (
                                <StatusIndicator type={getStatusBadgeType(item.status)}>
                                  {item.status || "Active"}
                                </StatusIndicator>
                              )
                            },
                            {
                              label: "Deployment",
                              value: (
                                <StatusIndicator type={getDeploymentBadgeType(item.deployment_status)}>
                                  {item.deployment_status || "not_deployed"}
                                </StatusIndicator>
                              )
                            },
                          ];
                          
                          return (
                            <div style={{ padding: "4px 0" }}>
                              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                                {gridItems.map((item, index) => (
                                  <div key={index}>
                                    <div style={{ fontSize: "13px", color: "#5f6b7a", marginBottom: "4px" }}>
                                      {item.label}
                                    </div>
                                    <div>{item.value}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        }
                      },
                      {
                        id: "actions",
                        content: (item) => (
                          <Box float="right">
                            <SpaceBetween size="xs" direction="horizontal">
                              <Button 
                                iconName="edit"
                                variant="icon"
                                onClick={() => {
                                  // Add debugging to see what's in the item object
                                  console.log("Edit button clicked for item:", item);
                                  
                                  // Vérification complète de l'item et ses propriétés
                                  if (!item) {
                                    console.error("Error: Item is undefined or null");
                                    setError("Cannot edit this agent. Invalid agent data.");
                                    return;
                                  }
                                  
                                  // Vérifier que l'ID existe (local ou adexcloud)
                                  const effectiveId = item.id || item.adexcloud_agent_id;
                                  if (!effectiveId) {
                                    console.error("Error: This agent doesn't have an ID");
                                    setError("Cannot edit this agent. Missing ID.");
                                    return;
                                  }

                                  console.log("Agent ID value:", effectiveId);
                                  console.log("creation_id value:", item.creation_id || "Not available");
                                  
                                  // Navigate to wizard in update mode if creation_id exists
                                  if (item.creation_id) {
                                    try {
                                      navigate(`/Wizard?creationId=${item.creation_id}&action=update`);
                                    } catch (error) {
                                      console.error("Error navigating to Wizard:", error);
                                      setError("An error occurred while trying to edit this agent. Please try again.");
                                    }
                                  } else {
                                    handleAgentSelect(item);
                                  }
                                }}
                              />
                              
                              <Button 
                                iconName="call" 
                                variant="icon"
                                disabled={item.status !== "success"}
                                onClick={() => {
                                  console.log("Test button clicked for agent:", item.id);
                                  handleAgentSelect(item);
                                  // Ouvrir directement l'onglet Test après la sélection
                                  setTimeout(() => {
                                    const testTabElement = document.querySelector('[data-testid="tabs"][id="test"]');
                                    if (testTabElement) {
                                      testTabElement.click();
                                    }
                                  }, 100);
                                }}
                              />
                              
                              <Popover
                                dismissButton={false}
                                position="top"
                                size="small"
                                triggerType="custom"
                                content={
                                  <SpaceBetween size="s">
                                    <Box variant="span">Are you sure you want to delete this agent?</Box>
                                    <Button variant="primary" onClick={() => handleDeleteAgent(item.id || item.adexcloud_agent_id)}>
                                      Confirm
                                    </Button>
                                  </SpaceBetween>
                                }
                              >
                                <Button iconName="remove" variant="icon" />
                              </Popover>
                            </SpaceBetween>
                          </Box>
                        )
                      },
                    ],
                  }}
                  cardsPerRow={[
                    { cards: 1 },            // Très petits écrans (< 480px): 1 carte par ligne
                    { minWidth: 480, cards: 2 }, // Petits écrans (≥ 480px): 2 cartes par ligne
                    { minWidth: 768, cards: 3 }, // Écrans moyens (≥ 768px): 3 cartes par ligne
                    { minWidth: 992, cards: 4 }  // Grands écrans (≥ 992px): 4 cartes par ligne
                  ]}
                  items={filteredAgents}
                  loadingText="Loading agents"
                  selectionType="single"
                  trackBy="_key"
                  visibleSections={["image", "details", "actions"]}
                  loading={loading}
                  empty={
                    <Box
                      margin={{ vertical: "xs" }}
                      textAlign="center"
                      color="inherit"
                    >
                      <SpaceBetween size="m">
                        <div>
                          <Box variant="h1" textAlign="center"><Icon name="user-profile" size="big" variant="normal" /></Box>
                          <b>No agents found</b>
                          <p>Create your first AI agent to get started.</p>
                        </div>
                        <Button variant="primary" onClick={() => navigate('/Wizard')}>Create Your First Agent</Button>
                      </SpaceBetween>
                    </Box>
                  }
                />
              </Grid>
              )}

              {viewMode === "table" && (
                <Table
                  onSelectionChange={({ detail }) => {
                    setSelectedItems(detail?.selectedItems ?? []);
                    if (detail.selectedItems.length > 0) {
                      handleAgentSelect(detail.selectedItems[0]);
                    }
                  }}
                  selectedItems={selectedItems}
                  selectionType="single"
                  trackBy="_key"
                  items={sortedAgents}
                  loadingText="Loading agents"
                  loading={loading}
                  stickyHeader
                  variant="container"
                  sortable
                  sortingColumn={sortingColumn}
                  sortingDescending={sortingDescending}
                  onSortingChange={({ detail }) => {
                    setSortingColumn(detail.sortingColumn);
                    setSortingDescending(detail.isDescending);
                  }}
                  columnDefinitions={[
                    {
                      id: "agent",
                      header: "Agent",
                      sortingField: "agent",
                      cell: (item) => {
                        const getBusinessImage = (typeName) => {
                          if (!typeName) return "/agent_receptionnist.png";
                          const t = typeName.toLowerCase();
                          if (t.includes("recept")) return "/agent_receptionnist.png";
                          if (t.includes("support")) return "/agent_support.png";
                          if (t.includes("sales") || t.includes("commerc")) return "/agent_commerciale.png";
                          return "/agent_receptionnist.png";
                        };
                        const getTypeIcon = (typeName) => {
                          if (!typeName) return "user-profile";
                          const t = typeName.toLowerCase();
                          if (t.includes("recept")) return "call";
                          if (t.includes("support")) return "contact";
                          if (t.includes("sales") || t.includes("commerc")) return "status-positive";
                          return "user-profile";
                        };
                        const mainImage = getBusinessImage(item.service_type || item.type);
                        const badgeIcon = getTypeIcon(item.type);

                        return (
                          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <div style={{ position: "relative", flexShrink: 0 }}>
                              <img
                                src={mainImage}
                                alt={item.service_type || item.type}
                                style={{
                                  height: "48px",
                                  width: "48px",
                                  objectFit: "contain",
                                  borderRadius: "8px",
                                  backgroundColor: "#f2f8ff",
                                }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                              <div style={{
                                position: "absolute",
                                bottom: "-6px",
                                right: "-6px",
                                width: "28px",
                                height: "28px",
                                borderRadius: "50%",
                                backgroundColor: "#ffffff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                border: "2px solid #ffffff",
                              }}>
                                <Icon name={badgeIcon} size="normal" variant="subtle" />
                              </div>
                            </div>
                            <div>
                              <div style={{ fontWeight: "bold", fontSize: "14px" }}>
                                {item.name?.replace("Assistant - ", "") || "Unnamed"}
                              </div>
                              <div style={{ fontSize: "12px", color: "#5f6b7a" }}>
                                {item.service_type || "General Assistant"}
                              </div>
                            </div>
                          </div>
                        );
                      },
                      isRowHeader: true,
                    },
                    {
                      id: "description",
                      header: "Description",
                      cell: (item) => (
                        <div style={{
                          maxWidth: "300px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          color: "#5f6b7a",
                          fontSize: "13px",
                        }}>
                          {item.description || item.system_prompt || "—"}
                        </div>
                      ),
                    },
                    {
                      id: "type",
                      header: "Business Type",
                      sortingField: "type",
                      cell: (item) => (
                        <Badge color="blue">{item.type || "—"}</Badge>
                      ),
                    },
                    {
                      id: "status",
                      header: "Status",
                      sortingField: "status",
                      cell: (item) => (
                        <StatusIndicator type={getStatusBadgeType(item.status)}>
                          {item.status || "Active"}
                        </StatusIndicator>
                      ),
                    },
                    {
                      id: "deployment",
                      header: "Deployment",
                      sortingField: "deployment",
                      cell: (item) => (
                        <StatusIndicator type={getDeploymentBadgeType(item.deployment_status)}>
                          {item.deployment_status || "not_deployed"}
                        </StatusIndicator>
                      ),
                    },
                    {
                      id: "phone",
                      header: "Phone",
                      sortingField: "phone",
                      cell: (item) => (
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          {isVoiceAgentType(item.ai_agent_type) ? (
                            item.phone_number && item.phone_number !== "Not assigned" ? (
                              <>
                                <Icon name="phone" size="small" />
                                <span>{item.phone_number}</span>
                              </>
                            ) : (
                              <span style={{ color: "#9ba7b6" }}>Not assigned</span>
                            )
                          ) : (
                            <span style={{ color: "#9ba7b6" }}>Text only</span>
                          )}
                        </div>
                      ),
                    },
                    {
                      id: "company",
                      header: "Company",
                      sortingField: "company",
                      cell: (item) => item.company_name || "N/A",
                    },
                    {
                      id: "created",
                      header: "Created",
                      sortingField: "created",
                      cell: (item) => (
                        <span style={{ fontSize: "13px", color: "#5f6b7a" }}>
                          {formatDate(item.createdAt)}
                        </span>
                      ),
                    },
                    {
                      id: "actions",
                      header: "Actions",
                      cell: (item) => (
                        <SpaceBetween size="xs" direction="horizontal">
                          <Button
                            iconName="edit"
                            variant="icon"
                            onClick={() => {
                              const effectiveId = item.id || item.adexcloud_agent_id;
                              if (!effectiveId) return;
                              if (item.creation_id) {
                                navigate(`/Wizard?creationId=${item.creation_id}&action=update`);
                              } else {
                                handleAgentSelect(item);
                              }
                            }}
                          />
                          <Button
                            iconName="call"
                            variant="icon"
                            disabled={item.status !== "success"}
                            onClick={() => {
                              handleAgentSelect(item);
                              setTimeout(() => {
                                const testTab = document.querySelector('[data-testid="tabs"][id="test"]');
                                if (testTab) testTab.click();
                              }, 100);
                            }}
                          />
                          <Popover
                            dismissButton={false}
                            position="top"
                            size="small"
                            triggerType="custom"
                            content={
                              <SpaceBetween size="s">
                                <Box variant="span">Delete this agent?</Box>
                                <Button variant="primary" onClick={() => handleDeleteAgent(item.id || item.adexcloud_agent_id)}>
                                  Confirm
                                </Button>
                              </SpaceBetween>
                            }
                          >
                            <Button iconName="remove" variant="icon" />
                          </Popover>
                        </SpaceBetween>
                      ),
                    },
                  ]}
                  columnDisplay={[
                    { id: "agent", visible: true },
                    { id: "description", visible: true },
                    { id: "type", visible: true },
                    { id: "status", visible: true },
                    { id: "deployment", visible: true },
                    { id: "phone", visible: true },
                    { id: "company", visible: true },
                    { id: "created", visible: true },
                    { id: "actions", visible: true },
                  ]}
                  empty={
                    <Box textAlign="center" color="inherit">
                      <SpaceBetween size="m">
                        <div>
                          <Box variant="h1" textAlign="center"><Icon name="user-profile" size="big" variant="normal" /></Box>
                          <b>No agents found</b>
                          <p>Create your first AI agent to get started.</p>
                        </div>
                        <Button variant="primary" onClick={() => navigate('/Wizard')}>Create Your First Agent</Button>
                      </SpaceBetween>
                    </Box>
                  }
                />
              )}
            </SpaceBetween>
          </ContentLayout>
        </div>
      }
      splitPanel={
        selectedAgent && (
          <SplitPanel
            header={
              <Header
                variant="h2"
                actions={
                  <SpaceBetween direction="horizontal" size="xs">
                    <Button
                      iconName="refresh"
                      loading={syncLoading}
                      onClick={handleSyncAgent}
                    >Sync</Button>
                    <Button iconName="remove" onClick={() => setIsSplitPanelOpen(false)}>Close</Button>
                  </SpaceBetween>
                }
              >
                {selectedAgent.name} Details
              </Header>
            }
          >
            <Tabs
              activeTabId={activeTabId}
              onChange={({ detail }) => setActiveTabId(detail.activeTabId)}
              tabs={[
                {
                  id: "details",
                  label: "Details",
                  content: (
                    <Container>
                      <SpaceBetween size="l">
                        {/* Basic Info */}
                        <Grid
                          gridDefinition={[
                            { colspan: { default: 6, xs: 6 } },
                            { colspan: { default: 6, xs: 6 } }
                          ]}
                        >
                          <div>
                            <Box variant="awsui-key-label">Agent Name</Box>
                            <div>{selectedAgent.name}</div>
                          </div>
                          <div>
                            <Box variant="awsui-key-label">Agent Type</Box>
                            <div>{selectedAgent.type || '—'}</div>
                          </div>
                          <div>
                            <Box variant="awsui-key-label">AI Agent Type</Box>
                            <div>
                              <Badge color={isVoiceAgent ? 'blue' : 'green'}>
                                {isVoiceAgent ? 'Voice' : (isTextAgent ? 'Text' : '—')}
                              </Badge>
                            </div>
                          </div>
                          <div>
                            <Box variant="awsui-key-label">Status</Box>
                            <StatusIndicator type={getStatusBadgeType(selectedAgent.status)}>
                              {selectedAgent.status || "Active"}
                            </StatusIndicator>
                          </div>
                          <div>
                            <Box variant="awsui-key-label">Deployment</Box>
                            <StatusIndicator type={getDeploymentBadgeType(selectedAgent.deployment_status)}>
                              {selectedAgent.deployment_status || "not_deployed"}
                            </StatusIndicator>
                          </div>
                          <div>
                            <Box variant="awsui-key-label">Service Type</Box>
                            <div>{selectedAgent.service_type || "General Assistant"}</div>
                          </div>
                          {isVoiceAgent && (
                            <div>
                              <Box variant="awsui-key-label">Phone Number</Box>
                              <div>{selectedAgent.phone_number || 'N/A'}</div>
                            </div>
                          )}
                          {isVoiceAgent && (
                            <div>
                              <Box variant="awsui-key-label">Voice</Box>
                              <div>{selectedAgent.voice || "Default"}</div>
                            </div>
                          )}
                          <div>
                            <Box variant="awsui-key-label">Company</Box>
                            <div>{selectedAgent.company_name || "N/A"}</div>
                          </div>
                          <div>
                            <Box variant="awsui-key-label">Local ID</Box>
                            <div>{selectedAgent.id || "N/A"}</div>
                          </div>
                          <div>
                            <Box variant="awsui-key-label">External ID</Box>
                            <div>{selectedAgent.adexcloud_agent_id || "N/A"}</div>
                          </div>
                          <div>
                            <Box variant="awsui-key-label">Created At</Box>
                            <div>{formatDate(selectedAgent.createdAt || selectedAgent.created_at)}</div>
                          </div>
                        </Grid>

                        {/* Usage */}
                        <Container header={<Header variant="h4">Usage & Quota</Header>}>
                          {usageLoading ? (
                            <Spinner size="normal" />
                          ) : agentUsage ? (
                            <SpaceBetween size="m">
                              <Grid gridDefinition={[{ colspan: { default: 6, xs: 6 } }, { colspan: { default: 6, xs: 6 } }]}>
                                {isVoiceAgent && (
                                  <div>
                                    <Box variant="awsui-key-label">Voice Minutes</Box>
                                    <div>{agentUsage.used_voice_minutes} / {agentUsage.allocated_voice_minutes} used</div>
                                    <Box variant="small" color={agentUsage.remaining_voice_minutes <= 0 ? "text-status-error" : "text-status-success"}>
                                      {agentUsage.remaining_voice_minutes} remaining
                                    </Box>
                                  </div>
                                )}
                                {isTextAgent && (
                                  <div>
                                    <Box variant="awsui-key-label">Text Conversations</Box>
                                    <div>{agentUsage.used_text_conversations} / {agentUsage.allocated_text_conversations} used</div>
                                    <Box variant="small" color={agentUsage.remaining_text_conversations <= 0 ? "text-status-error" : "text-status-success"}>
                                      {agentUsage.remaining_text_conversations} remaining
                                    </Box>
                                  </div>
                                )}
                                <div>
                                  <Box variant="awsui-key-label">Total {isVoiceAgent ? 'Calls' : 'Conversations'}</Box>
                                  <div>{agentUsage.total_calls}</div>
                                </div>
                                <div>
                                  <Box variant="awsui-key-label">Total Cost</Box>
                                  <div>{agentUsage.total_cost} {agentUsage?.currency || 'DZD'}</div>
                                </div>
                              </Grid>
                              {selectedAgent?.status === "suspended" && (
                                <Alert type="warning" header="Agent suspended">
                                  Voice or text quota has been exceeded. Buy additional quota to reactivate.
                                </Alert>
                              )}
                              <Button iconName="add-plus" onClick={() => { setQuotaForm({ voice_minutes: "", text_conversations: "" }); setIsQuotaModalVisible(true); }}>
                                Buy Quota
                              </Button>
                            </SpaceBetween>
                          ) : (
                            <Box variant="p" color="text-status-inactive">No usage data available.</Box>
                          )}
                        </Container>

                        {/* Last Call / Conversation */}
                        <Container header={<Header variant="h4">{isVoiceAgent ? 'Last Call' : 'Last Conversation'}</Header>}>
                          {usageLoading ? (
                            <Spinner size="normal" />
                          ) : agentUsage?.last_session ? (
                            <SpaceBetween size="m">
                              <Grid gridDefinition={[{ colspan: { default: 6, xs: 6 } }, { colspan: { default: 6, xs: 6 } }]}>
                                <div>
                                  <Box variant="awsui-key-label">{isVoiceAgent ? 'Call ID' : 'Session ID'}</Box>
                                  <div>{agentUsage.last_session.call_id}</div>
                                </div>
                                {isVoiceAgent && (
                                  <div>
                                    <Box variant="awsui-key-label">Duration</Box>
                                    <div>{agentUsage.last_session.duration_seconds}s</div>
                                  </div>
                                )}
                                <div>
                                  <Box variant="awsui-key-label">Customer</Box>
                                  <div>{agentUsage.last_session.customer_number || 'N/A'}</div>
                                </div>
                                <div>
                                  <Box variant="awsui-key-label">Reason</Box>
                                  <div>{agentUsage.last_session.call_ended_reason || 'N/A'}</div>
                                </div>
                                <div>
                                  <Box variant="awsui-key-label">Sentiment</Box>
                                  <div>{agentUsage.last_session.analysis?.sentiment_label || 'N/A'}</div>
                                </div>
                                <div>
                                  <Box variant="awsui-key-label">Satisfaction</Box>
                                  <div>{agentUsage.last_session.analysis?.customer_satisfaction ?? 'N/A'}/5</div>
                                </div>
                              </Grid>
                              {agentUsage.last_session.analysis?.summary && (
                                <div>
                                  <Box variant="awsui-key-label">Summary</Box>
                                  <Box variant="p">{agentUsage.last_session.analysis.summary}</Box>
                                </div>
                              )}
                              {isVoiceAgent && agentUsage.last_session.recording_url && (
                                <div>
                                  <Box variant="awsui-key-label">Recording</Box>
                                  <Link href={agentUsage.last_session.recording_url} external>
                                    Listen to recording
                                  </Link>
                                </div>
                              )}
                            </SpaceBetween>
                          ) : (
                            <Box variant="p" color="text-status-inactive">No call data available yet.</Box>
                          )}
                        </Container>

                        {/* Active Pack */}
                        <Container header={<Header variant="h4">Active Pack</Header>}>
                          {agentUsage?.pack ? (
                            <SpaceBetween size="m">
                              <Grid gridDefinition={[{ colspan: { default: 6, xs: 6 } }, { colspan: { default: 6, xs: 6 } }]}>
                                <div>
                                  <Box variant="awsui-key-label">Pack</Box>
                                  <div>{agentUsage.pack.pack_name}</div>
                                </div>
                                <div>
                                  <Box variant="awsui-key-label">Status</Box>
                                  <div>{agentUsage.pack.status}</div>
                                </div>
                                <div>
                                  <Box variant="awsui-key-label">Cycle Start</Box>
                                  <div>{agentUsage.pack.cycle_start ? formatDate(agentUsage.pack.cycle_start) : 'N/A'}</div>
                                </div>
                                <div>
                                  <Box variant="awsui-key-label">Cycle End</Box>
                                  <div>{agentUsage.pack.cycle_end ? formatDate(agentUsage.pack.cycle_end) : 'N/A'}</div>
                                </div>
                              </Grid>
                              <SpaceBetween size="xs" direction="horizontal">
                                <Button iconName="refresh" onClick={openReassignModal} loading={reassignLoading}>
                                  Reassign
                                </Button>
                                <Button iconName="close" onClick={handleReleasePack} loading={releaseLoading}>
                                  Release
                                </Button>
                              </SpaceBetween>
                            </SpaceBetween>
                          ) : (
                            <Box variant="p" color="text-status-error">No active pack assigned.</Box>
                          )}
                        </Container>

                        {selectedAgent.description && (
                          <Container header={<Header variant="h4">System Prompt</Header>}>
                            <Box variant="p">{selectedAgent.description}</Box>
                          </Container>
                        )}

                        <SpaceBetween size="m" direction="horizontal">
                          <Button 
                            iconName="edit"
                            onClick={() => {
                              if (!selectedAgent) {
                                console.error("Error: Agent is undefined or null");
                                setError("Cannot edit this agent. Invalid agent data.");
                                return;
                              }
                              
                              const effectiveAgentId = selectedAgent.id || selectedAgent.adexcloud_agent_id;
                              if (!effectiveAgentId) {
                                console.error("Error: This agent doesn't have an ID");
                                setError("Cannot edit this agent. Missing ID.");
                                return;
                              }

                              console.log("Agent ID value:", effectiveAgentId);
                              console.log("creation_id value:", selectedAgent.creation_id || "Not available");
                              
                              // Navigate to wizard in update mode if creation_id exists
                              if (selectedAgent.creation_id) {
                                try {
                                  navigate(`/Wizard?creationId=${selectedAgent.creation_id}&action=update`);
                                } catch (error) {
                                  console.error("Error navigating to Wizard:", error);
                                  setError("An error occurred while trying to edit this agent. Please try again.");
                                }
                              } else {
                                setError("Cannot edit this agent. Missing creation data. Please create a new agent instead.");
                              }
                            }}
                          >
                            Edit Agent
                          </Button>
                          <Button 
                            iconName="call" 
                            onClick={startAgentCall}
                            disabled={selectedAgent.status !== "success"}
                          >
                            Test {isVoiceAgent ? 'Call' : 'Chat'}
                          </Button>
                          <Button iconName="remove" variant="danger" onClick={() => handleDeleteAgent(selectedAgent.id || selectedAgent.adexcloud_agent_id)}>Delete</Button>
                        </SpaceBetween>
                      </SpaceBetween>
                    </Container>
                  ),
                },
                {
                  id: "widget",
                  label: "Widget",
                  content: (
                    <Container>
                      <SpaceBetween size="l">
                        <Alert type="info" header="Embeddable Widget">
                          Copy the code below and paste it into your website to embed the AdexGenie widget for this agent.
                        </Alert>
                        {widgetLoading ? (
                          <Spinner size="normal" />
                        ) : widgetCode ? (
                          <div style={{ position: 'relative' }}>
                            <pre
                              style={{
                                background: '#f4f4f4',
                                padding: '16px',
                                borderRadius: '8px',
                                overflow: 'auto',
                                fontSize: '12px',
                                maxHeight: '300px',
                              }}
                            >
                              <code>{widgetCode}</code>
                            </pre>
                            <div style={{ position: 'absolute', top: '8px', right: '8px' }}>
                              <Button iconName="copy" onClick={() => copyToClipboard(widgetCode)}>
                                Copy
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <Box variant="p" color="text-status-inactive">
                            No widget code available. The agent may not be deployed yet.
                          </Box>
                        )}
                      </SpaceBetween>
                    </Container>
                  ),
                },
                {
                  id: "stats",
                  label: "Statistics",
                  content: (
                    <Container>
                      <SpaceBetween size="l">
                        {/* Bannière de statistiques */}
                        <Alert
                          type="info"
                          header="Agent Performance Overview"
                        >
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Icon name="status-info" size="big" />
                            <span style={{ marginLeft: '10px' }}>Here's a summary of your agent's activity and performance metrics.</span>
                          </div>
                        </Alert>
                        
                        {/* Real usage metrics from agentUsage */}
                        <Grid
                          gridDefinition={[
                            { colspan: { default: 4, xs: 6 } },
                            { colspan: { default: 4, xs: 6 } },
                            { colspan: { default: 4, xs: 6 } }
                          ]}
                        >
                          <Container header={<Header variant="h4"><Icon name="call" size="small" /> Calls</Header>}>
                            <SpaceBetween size="m">
                              <Box textAlign="center">
                                <div style={{
                                  fontSize: "36px",
                                  fontWeight: "bold",
                                  color: "#0972d3",
                                  marginBottom: "8px"
                                }}>
                                  {agentUsage?.total_calls ?? selectedAgent.call_count ?? 0}
                                </div>
                                <Box variant="p">Total {isVoiceAgent ? 'Calls' : 'Conversations'}</Box>
                              </Box>
                            </SpaceBetween>
                          </Container>

                          {isVoiceAgent && (
                          <Container header={<Header variant="h4"><Icon name="microphone" size="small" /> Voice</Header>}>
                            <SpaceBetween size="m">
                              <Box textAlign="center">
                                <div style={{
                                  fontSize: "36px",
                                  fontWeight: "bold",
                                  color: "#0972d3",
                                  marginBottom: "8px"
                                }}>
                                  {agentUsage?.used_voice_minutes ?? 0}
                                </div>
                                <Box variant="p">Minutes Used</Box>
                              </Box>
                            </SpaceBetween>
                          </Container>
                          )}

                          {isTextAgent && (
                          <Container header={<Header variant="h4"><Icon name="envelope" size="small" /> Text</Header>}>
                            <SpaceBetween size="m">
                              <Box textAlign="center">
                                <div style={{
                                  fontSize: "36px",
                                  fontWeight: "bold",
                                  color: "#0972d3",
                                  marginBottom: "8px"
                                }}>
                                  {agentUsage?.used_text_conversations ?? selectedAgent.message_count ?? 0}
                                </div>
                                <Box variant="p">Conversations</Box>
                              </Box>
                            </SpaceBetween>
                          </Container>
                          )}
                        </Grid>

                        <Container header={<Header variant="h4">Activity Log</Header>}>
                          {activityLoading ? (
                            <Spinner size="normal" />
                          ) : activityLog.length > 0 ? (
                            <Table
                              columnDefinitions={[
                                {
                                  id: "action",
                                  header: "Action",
                                  cell: (item) => (
                                    <Badge color={
                                      item.action === "create" ? "green" :
                                      item.action === "deploy" ? "blue" :
                                      item.action === "suspend" ? "red" :
                                      item.action === "delete" ? "red" :
                                      item.action === "quota_bought" ? "purple" :
                                      item.action === "pack_assigned" ? "blue" :
                                      item.action === "pack_released" ? "grey" :
                                      item.action === "pack_reassigned" ? "blue" :
                                      item.action === "call_received" ? "green" :
                                      item.action === "consumption" ? "green" :
                                      "grey"
                                    }>
                                      {item.action_label}
                                    </Badge>
                                  ),
                                },
                                {
                                  id: "description",
                                  header: "Description",
                                  cell: (item) => item.description || "—",
                                },
                                {
                                  id: "date",
                                  header: "Date",
                                  cell: (item) => formatDate(item.created_at),
                                },
                              ]}
                              items={activityLog}
                              trackBy="id"
                              empty={
                                <Box variant="p" color="text-status-inactive" textAlign="center" padding="m">
                                  No activity recorded yet.
                                </Box>
                              }
                            />
                          ) : (
                            <Box variant="p" color="text-status-inactive" textAlign="center" padding="m">
                              No activity recorded yet. Actions like create, update, deploy, quota purchase, and calls will appear here.
                            </Box>
                          )}
                        </Container>
                      </SpaceBetween>
                    </Container>
                  ),
                },
                {
                  id: "test",
                  label: "Test Agent",
                  disabled: selectedAgent.status !== "success",
                  content: (
                    <Container>
                      <SpaceBetween size="l">
                        <Alert
                          type="success"
                          header="Your agent is ready to be tested!"
                        >
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Icon name="status-positive" size="big" />
                            <span style={{ marginLeft: '10px' }}>Test your agent in an isolated widget below. Click start to open a chat session, and end to close it.</span>
                          </div>
                        </Alert>

                        <Container
                          header={<Header variant="h4"><Icon name="information" size="small" /> Agent Details</Header>}
                        >
                          <ColumnLayout columns={{ default: 1, s: 1, m: 2, l: 2, xl: 2 }} variant="text-grid">
                            <div>
                              <Box variant="awsui-key-label">Agent Name</Box>
                              <div>{selectedAgent.name}</div>
                            </div>
                            <div>
                              <Box variant="awsui-key-label">Type</Box>
                              <Badge color={isVoiceAgent ? 'blue' : 'green'}>
                                {isVoiceAgent ? 'Voice' : 'Text'}
                              </Badge>
                            </div>
                            <div>
                              <Box variant="awsui-key-label">Status</Box>
                              <StatusIndicator type={getStatusBadgeType(selectedAgent.status)}>
                                {selectedAgent.status || "Active"}
                              </StatusIndicator>
                            </div>
                            <div>
                              <Box variant="awsui-key-label">Deployment</Box>
                              <StatusIndicator type={getDeploymentBadgeType(selectedAgent.deployment_status)}>
                                {selectedAgent.deployment_status || "not_deployed"}
                              </StatusIndicator>
                            </div>
                          </ColumnLayout>
                        </Container>

                        <AgentTestWidget agent={selectedAgent} />
                      </SpaceBetween>
                    </Container>
                  ),
                }
              ]}
            />
          </SplitPanel>
        )
      }
    />
    <Modal
      visible={isQuotaModalVisible}
      onDismiss={() => setIsQuotaModalVisible(false)}
      header="Buy Additional Quota"
      closeAriaLabel="Close modal"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={() => setIsQuotaModalVisible(false)}>Cancel</Button>
            <Button variant="primary" loading={quotaLoading} onClick={handleBuyQuota}>
              Buy for {estimatedQuotaCost()} DZD
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {isVoiceAgent && (
          <FormField label="Voice minutes">
            <Input
              type="number"
              value={quotaForm.voice_minutes}
              onChange={({ detail }) => setQuotaForm((p) => ({ ...p, voice_minutes: detail.value }))}
              placeholder="0"
            />
          </FormField>
        )}
        {isTextAgent && (
          <FormField label="Text conversations">
            <Input
              type="number"
              value={quotaForm.text_conversations}
              onChange={({ detail }) => setQuotaForm((p) => ({ ...p, text_conversations: detail.value }))}
              placeholder="0"
            />
          </FormField>
        )}
        <Box variant="p">
          {isVoiceAgent
            ? `Price: ${quotaPrices.voice_minute} DZD/minute`
            : `Price: ${quotaPrices.text_conversation} DZD/conversation`}
        </Box>
      </SpaceBetween>
    </Modal>

    <Modal
      visible={isReassignModalVisible}
      onDismiss={() => setIsReassignModalVisible(false)}
      header="Reassign pack to another agent"
      closeAriaLabel="Close"
      footer={
        <SpaceBetween direction="horizontal" size="xs">
          <Button variant="link" onClick={() => setIsReassignModalVisible(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={reassignLoading}
            disabled={!reassignTarget}
            onClick={handleReassignPack}
          >
            Reassign
          </Button>
        </SpaceBetween>
      }
    >
      <SpaceBetween size="m">
        <Box variant="p">
          Transfer the active pack from <Box variant="strong">{selectedAgent?.name || 'this agent'}</Box> to:
        </Box>
        <FormField label="Target agent">
          <Select
            selectedOption={reassignTarget}
            onChange={({ detail }) => setReassignTarget(detail.selectedOption)}
            options={agents
              .filter((a) => (a.id || a.adexcloud_agent_id) !== (selectedAgent?.id || selectedAgent?.adexcloud_agent_id))
              .map((a) => ({
                label: a.name || `Agent ${a.adexcloud_agent_id}`,
                value: a.id || a.adexcloud_agent_id,
              }))}
            placeholder="Choose an agent"
          />
        </FormField>
      </SpaceBetween>
    </Modal>
    </>
  );
};

export default AgentList;
